// file: components/SavedSearches.js
//
// Saved Search + My Areas.
//
// "My Areas" is a singleton SavedSearch (isMyAreas: true) holding up to 5
// areas a broker wants a personalized feed for. Named "Saved Searches" are
// arbitrary filter combos a broker stores from FilterPage's "Save Search"
// button. Both are served by the same backend model/controller — see
// backend/models/SavedSearch.js and backend/controllers/savedSearchController.js.
import React, { useCallback, useEffect, useState } from "react";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import {
  RefreshCw,
  Trash2,
  MapPin,
  Search as SearchIcon,
  Bookmark,
  ChevronDown,
} from "lucide-react";
import { Button } from "./ui/button";
import apiClient from "../common/apiClient";
import AreaPicker from "./AreaPicker";
import PropertyCard from "./PropertyCard";
import { useUserProfile, getCityOptions } from "../contexts/UserProfileContext";
import "./SavedSearches.css";

const USER_BASE = "/cjidnvij/ceksfbuebijn/user";
const MAX_AREAS = 5;

// The app-wide city (set by Owner/Broker Properties) when the user can access it,
// else their first PropertyAccessCity. Reads the cookie, so it's right on first
// paint and a Pune-only user never briefly loads Ahmedabad's areas.
function getInitialCity() {
  const options = getCityOptions();
  const stored = (localStorage.getItem("selectedCity") || "").trim().toLowerCase();
  return options.find((c) => c.toLowerCase() === stored) || options[0];
}

function authHeaders() {
  return { headers: { Authorization: `Bearer ${Cookies.get("token")}` } };
}

// Same shaping MatchProperty.js applies before handing a raw API property to
// PropertyCard — duplicated locally rather than imported since it isn't
// exported from that file.
function transformPropertyData(property) {
  if (!property) return {};
  const price =
    property.price || property.rent || property.rentValue || property.expectedPrice || "NA";

  return {
    _id: property._id || property.id,
    propertyId: property.propertyId || property._id,
    title: property.title || property.propertyTitle || "No Title",
    type: property.type || property.propertyType || "Not Available",
    price,
    rentValue: property.rentValue || price,
    unitType: property.unitType || property.subType || "NA",
    area: property.area || property.location || "NA",
    bhk: property.bhk || property.bedrooms || "NA",
    furnishedType: property.furnishedType || property.furnishing || "NA",
    address: property.address || property.fullAddress || "NA",
    sqFt: property.sqFt || property.squareFt || property.areaSize || "NA",
    description: property.description || property.propertyDescription || "NA",
    listedDate: property.listedDate || property.createdAt || new Date(),
    verifyStatus: property.verifyStatus || "unverified",
    media: property.media || property.images || [],
  };
}

const SavedSearches = () => {
  // ---------------- My Areas ----------------
  const { hasCityAccess, propertyAccessCities } = useUserProfile();
  const [myAreasCity, setMyAreasCity] = useState(getInitialCity);
  const [myAreas, setMyAreas] = useState([]);
  const [isLoadingMyAreas, setIsLoadingMyAreas] = useState(true);
  const [isSavingMyAreas, setIsSavingMyAreas] = useState(false);
  // The picker is a tall block; keep it collapsed once areas are chosen so the
  // feed below is what the broker lands on. Opened on first load only when
  // there is nothing picked yet (see loadMyAreas).
  const [isAreaPickerOpen, setIsAreaPickerOpen] = useState(false);

  const [feedProperties, setFeedProperties] = useState([]);
  const [isLoadingFeed, setIsLoadingFeed] = useState(false);
  const [contactInfoMap, setContactInfoMap] = useState({});

  // ---------------- Saved Searches ----------------
  const [savedSearches, setSavedSearches] = useState([]);
  const [isLoadingSearches, setIsLoadingSearches] = useState(true);
  const [expandedSearchId, setExpandedSearchId] = useState(null);
  const [expandedMatches, setExpandedMatches] = useState({});
  const [isLoadingMatches, setIsLoadingMatches] = useState(false);

  const loadMyAreas = useCallback(async () => {
    setIsLoadingMyAreas(true);
    const result = await apiClient("get", `${USER_BASE}/saved-searches/my-areas`, {}, authHeaders());
    if (result.success) {
      const areas = result.data?.data?.areas || [];
      setMyAreas(areas);
      if (areas.length === 0) setIsAreaPickerOpen(true);
    }
    setIsLoadingMyAreas(false);
  }, []);

  const loadFeed = useCallback(async () => {
    setIsLoadingFeed(true);
    const result = await apiClient(
      "get",
      `${USER_BASE}/saved-searches/my-areas/feed`,
      { limit: 12 },
      authHeaders()
    );
    if (result.success) {
      setFeedProperties(result.data?.data?.properties || []);
    }
    setIsLoadingFeed(false);
  }, []);

  const loadSavedSearches = useCallback(async () => {
    setIsLoadingSearches(true);
    const result = await apiClient("get", `${USER_BASE}/saved-searches/my-list`, {}, authHeaders());
    if (result.success) {
      setSavedSearches(result.data?.data || []);
    }
    setIsLoadingSearches(false);
  }, []);

  useEffect(() => {
    loadMyAreas();
    loadSavedSearches();
  }, [loadMyAreas, loadSavedSearches]);

  // Once the profile loads, switch to the first accessible city if the current
  // one isn't in PropertyAccessCity (e.g. the cookie wasn't written yet).
  useEffect(() => {
    if (propertyAccessCities.length === 0) return; // not loaded yet
    if (hasCityAccess(myAreasCity)) return;
    setMyAreasCity(propertyAccessCities[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyAccessCities.join("|")]);

  useEffect(() => {
    if (myAreas.length > 0) loadFeed();
    else setFeedProperties([]);
  }, [myAreas, loadFeed]);

  const saveMyAreas = async () => {
    setIsSavingMyAreas(true);
    const result = await apiClient(
      "put",
      `${USER_BASE}/saved-searches/my-areas`,
      { areas: myAreas },
      authHeaders()
    );
    setIsSavingMyAreas(false);

    if (result.success) {
      toast.success("My Areas saved.");
      loadFeed();
    } else {
      toast.error(result.error?.message || "Could not save My Areas.");
    }
  };

  const deleteSavedSearch = async (id) => {
    const result = await apiClient("delete", `${USER_BASE}/saved-searches/${id}`, {}, authHeaders());
    if (result.success) {
      setSavedSearches((prev) => prev.filter((s) => s._id !== id));
      toast.success("Saved search deleted.");
    } else {
      toast.error(result.error?.message || "Could not delete this saved search.");
    }
  };

  const toggleExpandSearch = async (search) => {
    if (expandedSearchId === search._id) {
      setExpandedSearchId(null);
      return;
    }

    setExpandedSearchId(search._id);

    // Mark matches seen so the "new" badge clears once the broker looks.
    apiClient("post", `${USER_BASE}/saved-searches/${search._id}/matches/seen`, {}, authHeaders());

    if (expandedMatches[search._id]) return;

    setIsLoadingMatches(true);
    const result = await apiClient(
      "get",
      `${USER_BASE}/saved-searches/${search._id}/matches`,
      {},
      authHeaders()
    );
    setIsLoadingMatches(false);

    if (result.success) {
      const data = result.data?.data || {};
      const combined = [...(data.ownerProperties || []), ...(data.brokerProperties || [])];
      setExpandedMatches((prev) => ({ ...prev, [search._id]: combined }));
    } else {
      toast.error(result.error?.message || "Could not load matches for this search.");
    }
  };

  const summarizeSearch = (search) => {
    const parts = [];
    if (search.type) parts.push(search.type);
    if (search.areas?.length) parts.push(search.areas.join(", "));
    if (search.bhk?.length) parts.push(search.bhk.join(", "));
    if (search.priceRange?.max) {
      parts.push(`up to ₹${Number(search.priceRange.max).toLocaleString("en-IN")}`);
    }
    return parts.join(" · ") || "Any property";
  };

  return (
    <div className="saved-searches space-y-4 sm:space-y-6">
      <h1 className="text-xl sm:text-2xl font-bold">Saved Searches</h1>

      {/* ---------------- My Areas ---------------- */}
      <div className="bg-card rounded-lg p-3 sm:p-5 shadow-card border border-border space-y-3 sm:space-y-4">
        {/* The whole header row is the collapse toggle. The hint keeps a full
            row of its own (w-full) instead of being squeezed onto the title
            line, which also leaves ml-auto free to pin the chevron to the right
            end of that title line. */}
        <button
          type="button"
          onClick={() => setIsAreaPickerOpen((open) => !open)}
          aria-expanded={isAreaPickerOpen}
          className="w-full flex flex-wrap items-center gap-x-2 gap-y-1 text-left"
        >
          <MapPin size={18} className="text-primary shrink-0" />
          <h2 className="text-base sm:text-lg font-semibold">My Areas</h2>
          <ChevronDown
            size={18}
            className={`ml-auto shrink-0 text-muted-foreground transition-transform ${
              isAreaPickerOpen ? "rotate-180" : ""
            }`}
          />
          {/* Collapsed, the chosen area names stand in for the picker so the
              broker can see what the feed below is filtered to without
              reopening it. Their own row (w-full) — five names would otherwise
              push the chevron off the title line. */}
          {!isAreaPickerOpen && myAreas.length > 0 && (
            <span className="w-full flex flex-wrap gap-1.5">
              {myAreas.map((area) => (
                <span
                  key={area}
                  className="max-w-full truncate text-xs font-medium px-2.5 py-1 rounded-full bg-purple-600 text-accent-foreground"
                >
                  {area}
                </span>
              ))}
            </span>
          )}
          {isAreaPickerOpen && (
            <span className="w-full text-xs text-muted-foreground">
              (pick up to {MAX_AREAS} — you'll see newly listed properties here first)
            </span>
          )}
        </button>

        {isAreaPickerOpen && (
          <>
            {/*
              Areas are stored per city AND per moduleType. "owner" is the set the
              admin panel keeps populated for all three cities, so My Areas reads
              that list — "requirement" only has areas for Ahmedabad West, which left
              Ahmedabad East and Gandhinagar showing "No areas found".
            */}
            <AreaPicker
              city={myAreasCity}
              onCityChange={setMyAreasCity}
              areas={myAreas}
              onAreasChange={setMyAreas}
              moduleType="owner"
              maxAreas={MAX_AREAS}
              onMaxAreasExceeded={() => toast.error(`You can pick up to ${MAX_AREAS} areas.`)}
            />

            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <Button
                size="sm"
                className="gradient-primary border-0 text-primary-foreground w-full sm:w-auto"
                disabled={isSavingMyAreas || isLoadingMyAreas}
                onClick={saveMyAreas}
              >
                {isSavingMyAreas ? "Saving..." : "Save My Areas"}
              </Button>
              <span className="text-xs text-muted-foreground">
                {myAreas.length}/{MAX_AREAS} selected
              </span>
            </div>
          </>
        )}
      </div>

      {/* ---------------- New in Your Areas feed ---------------- */}
      {myAreas.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-base sm:text-lg font-semibold">New in Your Areas</h2>
            <button
              onClick={loadFeed}
              disabled={isLoadingFeed}
              className="shrink-0 rounded-lg border border-gray-300 p-2.5 text-gray-600 hover:text-primary disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw className={`h-4 w-4 ${isLoadingFeed ? "animate-spin" : ""}`} />
            </button>
          </div>

          {isLoadingFeed && feedProperties.length === 0 ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : feedProperties.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No new properties in your areas yet — check back soon.
            </p>
          ) : (
            /* Cards run edge to edge on phones: -mx-4 cancels the `p-4` gutter
               DashboardLayout puts on <main>, and md:mx-0 leaves the wider
               md:p-6 desktop gutter alone. */
            <div className="feed-card-list -mx-4 md:mx-0 space-y-3">
              {feedProperties.map((property) => (
                <PropertyCard
                  key={property._id}
                  property={transformPropertyData(property)}
                  contactInfoMap={contactInfoMap}
                  setContactInfoMap={setContactInfoMap}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ---------------- Saved Searches list ---------------- */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Bookmark size={18} className="text-primary shrink-0" />
          <h2 className="text-base sm:text-lg font-semibold">Your Saved Searches</h2>
        </div>

        {isLoadingSearches ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : savedSearches.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No saved searches yet. Save a filter combo from the Search page to see it here.
          </p>
        ) : (
          savedSearches.map((search) => (
            <div key={search._id} className="glass-card rounded-xl overflow-hidden">
              {/* Name/summary stack above the actions on phones; side by side
                  from sm up. min-w-0 + break-words keep a long saved-search
                  name or area list from forcing the card wider than the screen. */}
              <div className="p-3 sm:p-4 border-b border-border flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                <div className="min-w-0">
                  <h3 className="font-semibold text-sm sm:text-base flex items-start gap-2">
                    <SearchIcon size={14} className="text-muted-foreground shrink-0 mt-1" />
                    <span className="break-words">{search.name || "Untitled search"}</span>
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground mt-1 break-words">
                    {summarizeSearch(search)}
                  </p>
                </div>
                <div className="flex items-center flex-wrap gap-2 sm:shrink-0">
                  {search.unreadMatchCount > 0 && (
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-red-100 text-red-700 whitespace-nowrap">
                      {search.unreadMatchCount} new
                    </span>
                  )}
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-primary-light text-accent-foreground whitespace-nowrap">
                    {search.matchCount || 0} matches
                  </span>
                  {/* ml-auto pins the two controls to the right edge on mobile,
                      where the badges sit on the same wrapped row. */}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="ml-auto sm:ml-0 whitespace-nowrap"
                    onClick={() => toggleExpandSearch(search)}
                  >
                    {expandedSearchId === search._id ? "Hide" : "View Matches"}
                  </Button>
                  <button
                    onClick={() => deleteSavedSearch(search._id)}
                    className="shrink-0 p-2.5 rounded-lg text-muted-foreground hover:text-red-600 hover:bg-red-50"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {expandedSearchId === search._id && (
                <div className="p-2 sm:p-4 bg-muted/30 space-y-3">
                  {isLoadingMatches && !expandedMatches[search._id] ? (
                    <p className="text-sm text-muted-foreground">Loading matches...</p>
                  ) : (expandedMatches[search._id] || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground">No matches yet for this search.</p>
                  ) : (
                    expandedMatches[search._id].map((property) => (
                      <PropertyCard
                        key={property._id}
                        property={transformPropertyData(property)}
                        contactInfoMap={contactInfoMap}
                        setContactInfoMap={setContactInfoMap}
                      />
                    ))
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SavedSearches;
